import { ChevronDown } from '@aktiia/design-system';
import { addDays, differenceInYears, format } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EnvVariable, ModalName } from '../../../../../enums';
import { useOpenModal } from '../../../../../features/modals';
import { useOrganization } from '../../../../../features/organization';
import { getPatientName } from '../../../../../features/patients';
import { IAverageMeasurement } from '../../../../../interfaces';
import {
  capitalize,
  formatHeightInStandard,
  formatInStandard,
  formatWeightInStandard,
  getBooleanEnvVariable,
  parseDate,
  toUTCDate,
} from '../../../../../utils';
import { Button, Card } from '../../../../common';
import { BpStatus } from '../../../bp-status';
import { InlineFormField } from '../../../inline-form-field';
import { SyncStatus } from '../../../sync-status';
import { IPatientSummaryProps } from './types';

export const PatientSummary = ({ patient }: IPatientSummaryProps) => {
  const { t } = useTranslation();
  const { organization } = useOrganization();
  const openFeatureFeedbackModal = useOpenModal(ModalName.FEATURE_FEEDBACK);
  const openLearnMoreModal = useOpenModal(ModalName.LEAR_MORE);
  const age = useMemo(() => (patient ? differenceInYears(Date.now(), parseDate(patient.birthday)) : '-'), [patient]);
  const lastSyncDate = useMemo(
    () => (patient?.bloodPressure?.lastSync ? toUTCDate(patient.bloodPressure.lastSync) : null),
    [patient],
  );
  const canChooseAction = getBooleanEnvVariable(EnvVariable.USE_CHOOSE_ACTION);
  const canSendMessage = getBooleanEnvVariable(EnvVariable.USE_SEND_MESSAGE);

  const averageSummary = useMemo(() => {
    const { sys, dia } = patient?.bloodPressure.avg || ({} as IAverageMeasurement);

    if (sys === null || dia === null || Number.isNaN(sys) || Number.isNaN(dia)) {
      return t('patient.label.bpAverage.unknown');
    }

    return t('patient.label.bpAverage.value', { sys: Math.round(sys), dia: Math.round(dia) });
  }, [patient?.bloodPressure.avg]);

  const timeSpent = useMemo(() => {
    const { timeInTargetRange } = patient?.bloodPressure || {};

    if (timeInTargetRange === null) {
      return t('patient.label.bpPercentage.unknown');
    }

    return t('patient.label.bpPercentage.value', { value: timeInTargetRange });
  }, [patient?.bloodPressure]);

  return (
    <div className="grid grid-cols-3 gap-6 mb-6 patient-summary">
      <Card className="justify-between items-start">
        <div className="w-full">
          <h3 className="typography-heading-large text-text-primary mb-4" style={{ wordBreak: 'break-word' }}>
            {patient ? getPatientName(patient) : '-'}
          </h3>
          <InlineFormField title={t('patient.field.email')} value={patient?.username} />
          <InlineFormField title={t('patient.field.organization')} value={organization?.name} />
          <InlineFormField title={t('patient.field.syncState')} bottomBorder={false}>
            {patient && <SyncStatus patient={patient} showSubtitle={true} />}
          </InlineFormField>
        </div>
        {canSendMessage && (
          <Button
            text={t('patient.button.message')}
            size="sm"
            className="send-message-button mt-6"
            onClick={() => openFeatureFeedbackModal()}
          />
        )}
      </Card>

      <Card>
        <div className="flex justify-between mb-2">
          <h3 className="typography-heading-2 text-text-primary">{t('patient.title.summary')}</h3>
          <Button
            text={t('patient.button.more')}
            type="tertiary"
            size="sm"
            className="learn-more-btn pdf-hide modal-btn"
            onClick={() => openLearnMoreModal()}
          />
        </div>
        <p className="typography-paragraph-4 text-text-tertiary mb-3">
          {lastSyncDate
            ? t('patient.label.average.description', {
                from: format(addDays(lastSyncDate, -6), 'd MMM'),
                to: format(lastSyncDate, 'd MMM'),
                time: format(lastSyncDate, "h:mmaaaaa'm'"),
              })
            : t('patient.label.average.unknown')}
        </p>
        <div className="pt-3 mb-2 typography-heading-6 text-text-secondary border-t border-border-medium">
          {t('patient.label.bpInfo')}
        </div>
        <InlineFormField
          className="py-2"
          title={t('patient.field.bpAverage')}
          value={averageSummary}
          bottomBorder={false}
        />
        <InlineFormField
          className="py-2"
          title={t('patient.field.bpPercentage')}
          value={timeSpent}
          bottomBorder={false}
        />
        <InlineFormField className="py-2" title={t('patient.field.bpType')} bottomBorder={false}>
          {patient && (
            <BpStatus disabled={true} status={patient.bloodPressure.status} isUS={patient.countryKey === 'usa'} />
          )}
        </InlineFormField>
        {canChooseAction && (
          <div className="actions-for-patient flex items-center justify-between pt-4 mt-2.5 border-t border-border-medium">
            <span className="typography-heading-6 text-text-secondary">{t('patient.label.actions')}</span>
            <Button
              text={t('patient.button.action')}
              size="sm"
              rightIcon={<ChevronDown className="scale-75" />}
              onClick={() => openFeatureFeedbackModal()}
            />
          </div>
        )}
      </Card>

      <Card>
        <h3 className="typography-heading-2 text-text-primary mb-2">{t('patient.title.profile')}</h3>
        <InlineFormField
          title={t('patient.field.weight')}
          value={patient ? formatWeightInStandard(patient.weight, patient.system) : '-'}
        />
        <InlineFormField
          title={t('patient.field.height')}
          value={patient ? formatHeightInStandard(patient.height, patient.system) : '-'}
        />
        <InlineFormField title={t('patient.field.gender')} value={patient ? capitalize(patient.gender) : '-'} />
        <InlineFormField title={t('patient.field.age')} value={age} />
        <InlineFormField
          title={t('patient.field.birthday')}
          value={patient ? formatInStandard(parseDate(patient.birthday), 'd MMM yyyy', 'MM/dd/yyyy') : '-'}
          bottomBorder={false}
        />
      </Card>
    </div>
  );
};
